import posthog from 'posthog-js';
import dayjs from '@/libs/dayjs';
import { BETA_KEYS, FEATURE_FLAGS, FENIX_ONLY_WORKSPACE_MIN_DATE } from '@/constants';
import { POSTHOG_ENABLED } from '@/providers/posthog-provider';
import { SessionQuery } from '@/graphql/generated/session.generated';
import getBetaStatus from './get-beta-status';
import { isFenixExclusive, isSwonkieDevWorkspace } from '@/utils/users-path-filter';

export type Workspace = SessionQuery['currentWorkspace'];

/**
 * A feature flag that hasn't resolved yet (or that PostHog never answered because it is blocked)
 * comes back as `undefined`. Treating that as "off" is what serves legacy to ad-blocked users, so
 * only an explicit `false` counts as disabled.
 */
const flagIsNotDisabled = (flag: string) => !POSTHOG_ENABLED || posthog.isFeatureEnabled(flag) !== false;

/** Workspaces created on/after the cutoff. They only ever see fenix - no legacy, no switch. */
export function isFenixOnlyWorkspace(workspace: Workspace): boolean {
    return !dayjs.utc(workspace.createdAt).isBefore(dayjs.utc(FENIX_ONLY_WORKSPACE_MIN_DATE));
}

/** Cohorts that get fenix regardless of what this browser has stored in `enabledBetas`. */
function isFenixForcedWorkspace(workspace: Workspace): boolean {
    return (
        isFenixOnlyWorkspace(workspace) ||
        isSwonkieDevWorkspace(workspace) ||
        isFenixExclusive(workspace)
    );
}

/**
 * Whether to render the fenix post-creation flow. Forced cohorts bypass `enabledBetas` entirely -
 * that storage is per-browser, so a new workspace opened in a browser carrying an older user's
 * value would otherwise land on legacy with no way out.
 */
export function isUsingComposeBeta(workspace: Workspace): boolean {
    if (isFenixForcedWorkspace(workspace)) return true;
    return flagIsNotDisabled(FEATURE_FLAGS.COMPOSE_BETA) && getBetaStatus(BETA_KEYS.COMPOSE_BETA);
}

/** Whether this workspace may switch between the fenix and legacy post-creation flows. */
export function canSwitchComposeVersion(workspace: Workspace): boolean {
    return flagIsNotDisabled(FEATURE_FLAGS.COMPOSE_BETA) && !isFenixForcedWorkspace(workspace);
}

/**
 * Whether to render the new dashboards rather than the legacy reports. Unlike the compose beta,
 * this keeps the original strict flag check - the fail-open behaviour above is a deliberate fix
 * for the legacy post-creation flow (DEV-4478) and is not being extended to reports here.
 */
export function isUsingInsightsBeta(workspace: Workspace): boolean {
    if (isSwonkieDevWorkspace(workspace) || isFenixExclusive(workspace)) return true;
    return (
        POSTHOG_ENABLED &&
        !posthog.isFeatureEnabled(FEATURE_FLAGS.LEGACY_REPORTS) &&
        getBetaStatus(BETA_KEYS.REPORTS_BETA)
    );
}

/** Whether this workspace may switch between the new dashboards and the legacy reports. */
export function canSwitchInsightsVersion(workspace: Workspace): boolean {
    return (
        POSTHOG_ENABLED &&
        !posthog.isFeatureEnabled(FEATURE_FLAGS.LEGACY_REPORTS) &&
        !isSwonkieDevWorkspace(workspace) &&
        !isFenixExclusive(workspace)
    );
}
